export const RECOVERY_REASON_CODES = [
  'LOST_DEVICE',
  'SYNC_FAILURE',
  'OPERATOR_UNAVAILABLE',
  'RETAILER_DEADLINE',
  'EMERGENCY',
  'EXTERNAL_EVIDENCE',
  'OTHER',
] as const;

export type RecoveryReasonCode = (typeof RECOVERY_REASON_CODES)[number];

export const RECOVERY_SESSION_STATUSES = ['open', 'closed', 'voided', 'expired'] as const;
export type RecoverySessionStatus = (typeof RECOVERY_SESSION_STATUSES)[number];

export const RECOVERY_ACTION_TYPES = [
  'upload_evidence',
  'record_quantities',
  'add_note',
  'resolve_task',
  'publish_evidence',
  'store_manager_signoff',
] as const;

export type RecoveryActionType = (typeof RECOVERY_ACTION_TYPES)[number];

import type { RecoveryVerdict } from './admin-recovery-discrepancy.js';

export {
  RECOVERY_VERDICTS,
  type RecoveryVerdict,
  isTaskClosedByAdministrativeRecovery,
  isStoreEligibleForAdministrativeRecovery,
  STORE_COMPLETE_TASK_STATUSES_FOR_RECOVERY,
} from './admin-recovery-discrepancy.js';
export type { RecoveryFormatEvidenceRow } from './admin-recovery-discrepancy.js';

export interface OpenRecoverySessionRequest {
  cycleId: number;
  storeId: number;
  assignedOperatorId: number;
  reasonCode: RecoveryReasonCode;
  reasonNotes?: string;
  externalReference?: string;
  auditAcknowledged: boolean;
}

export interface RecoverySessionDto {
  id: number;
  cycleId: number;
  storeId: number;
  retailerId: number | null;
  assignedOperatorId: number;
  assignedOperatorName: string | null;
  openedByAdminId: number;
  openedByAdminName: string | null;
  reasonCode: string;
  reasonNotes: string | null;
  externalReference: string | null;
  status: RecoverySessionStatus;
  openedAt: string;
  closedAt: string | null;
  expiresAt: string | null;
  closeSummary: string | null;
  storeName?: string | null;
}

export interface RecoveryStoreTaskDto {
  campaignTaskId: number;
  campaignId: number;
  campaignName: string;
  packId: number | null;
  packName: string | null;
  taskType: string;
  status: string;
  targetQuantity: number;
  resetGeneration: number;
  resolvedByOverrideId: number | null;
  hasActiveOverride: boolean;
  /** Last recovery resolve verdict when task was closed via recovery */
  recoveryVerdict?: RecoveryVerdict | null;
  /** Operator mobile submission when synced (sign_off_type = operator). */
  operatorProgress?: RecoveryOperatorProgressDto | null;
}

/** Stored on admin_recovery_evidence.meta and used for storage path organisation */
export interface RecoveryEvidenceMeta {
  source: 'admin_recovery';
  sessionId: number;
  campaignTaskId: number;
  storeId: number;
  storeName: string;
  campaignId: number;
  campaignName: string;
  cycleId: number;
  cycleName: string | null;
  operatorId: number;
  operatorName: string | null;
  adminId: number;
  adminName: string | null;
  originalName?: string;
}

export interface RecoveryResolveTaskRequest {
  campaignTaskId: number;
  verdict: RecoveryVerdict;
  postedUnits?: number;
  formatQuantities?: Record<string, number>;
  /** Per-format discrepancy reasons when delivered ≠ planned */
  formatDiscrepancyReasons?: Record<string, string>;
  notes?: string;
  /** Required when verdict is REJECT */
  rejectionNotes?: string;
}

export interface RecoveryFormatPlanRow {
  formatId: number;
  formatName: string;
  planned: number;
  /** Delivered quantity from operator mobile submission, when synced. */
  operatorDelivered?: number | null;
}

export interface RecoveryOperatorProgressPhoto {
  id: number;
  secureUrl: string;
  thumbUrl: string;
}

export interface RecoveryOperatorProgressDto {
  hasOperatorSubmission: boolean;
  photoCount: number;
  photos: RecoveryOperatorProgressPhoto[];
  thumbUrl: string | null;
  deliveredByFormatId: Record<number, number>;
  totalDelivered: number | null;
  installerNote: string | null;
  submittedAt: string | null;
  postingEventId: number | null;
}

export interface RecoveryTaskFormatPlanResponse {
  success: boolean;
  campaignTaskId: number;
  campaignName: string;
  packName: string | null;
  taskType: string;
  targetQuantity: number;
  isFlexible: boolean;
  selectedGroupName: string | null;
  formats: RecoveryFormatPlanRow[];
  /** True when a mobile_assignments row exists for this task scope. */
  assignmentFound: boolean;
  warning?: string;
  /** Operator mobile submission summary for this task. */
  operatorProgress?: RecoveryOperatorProgressDto | null;
}

export interface RecoveryStoreManagerSignatureDto {
  hasSignature: boolean;
  signatureUrl: string | null;
  signoffMethod: 'signature' | 'typed_name' | null;
  signedBy: string | null;
  signedAt: string | null;
  submissionSource: string | null;
}

export function mapRecoveryStoreManagerSignature(
  visit: {
    signedAt?: Date | string | null;
    signatureUrl?: string | null;
    signoffMethod?: string | null;
    signedBy?: string | null;
    submissionSource?: string | null;
  } | null | undefined,
): RecoveryStoreManagerSignatureDto {
  const signedAtValue = visit?.signedAt
    ? visit.signedAt instanceof Date
      ? visit.signedAt.toISOString()
      : String(visit.signedAt)
    : null;

  if (!signedAtValue) {
    return {
      hasSignature: false,
      signatureUrl: null,
      signoffMethod: null,
      signedBy: null,
      signedAt: null,
      submissionSource: visit?.submissionSource ?? null,
    };
  }

  return {
    hasSignature: true,
    signatureUrl: visit?.signatureUrl ?? null,
    signoffMethod: visit?.signoffMethod === 'typed_name' ? 'typed_name' : 'signature',
    signedBy: visit?.signedBy ?? null,
    signedAt: signedAtValue,
    submissionSource: visit?.submissionSource ?? null,
  };
}

export type RecoveryHeldSignoff =
  | { state: 'unavailable' }
  | { state: 'none' }
  | { state: 'pending'; id: number; capturedAt: string; receivedAt: string;
      incompleteTaskCount: number; promotionEnabled: boolean };

export interface RecoveryStoreContextResponse {
  heldSignoff?: RecoveryHeldSignoff;
  cycleId: number;
  storeId: number;
  storeName: string;
  assignedOperatorId: number;
  assignedOperatorName: string | null;
  openSession: RecoverySessionDto | null;
  tasks: RecoveryStoreTaskDto[];
  storeManagerSignature: RecoveryStoreManagerSignatureDto;
  /** False when every active task is already operator/admin complete — recovery is closed. */
  recoveryEligible: boolean;
  recoveryIneligibleReason?: string | null;
}

export interface RecoveryStoreSignoffUploadResponse {
  success: boolean;
  actionId: number;
  storeManagerSignature: RecoveryStoreManagerSignatureDto;
  replacedExisting: boolean;
}

export interface RecoveryActionDto {
  id: number;
  sessionId: number;
  campaignTaskId: number;
  actionType: RecoveryActionType;
  payload: Record<string, unknown>;
  resolutionStatus: string;
  createdAt: string;
}

export interface RecoveryEvidenceDto {
  id: number;
  sessionId: number;
  actionId: number | null;
  publicId: string;
  secureUrl: string;
  assetType: string;
  createdAt: string;
}

export interface RecoverySessionDetailResponse extends RecoverySessionDto {
  actions: RecoveryActionDto[];
  evidence: RecoveryEvidenceDto[];
}

export type { RecoveryProofPublishStatus } from './admin-recovery-proof-publish.js';

export interface RecoveryProofPublishResponse {
  postingEventId: number;
  evidenceAssetCount: number;
  resolveActionId: number | null;
  alreadyPublished: boolean;
  reviewApprovalSkipped: boolean;
}
